/**
 * Deviation-based DCA / sell simulation aligned with intrinsic lab price rows.
 * — While (price − intrinsic) / intrinsic < 0: invest up to `dcaDailyUsd` per bar from cash.
 * — When deviation ≥ 0: sell all shares at that bar’s close.
 * Bars with no intrinsic (null) do not trade; equity is still marked to market.
 *
 * Benchmark: mean length of EOD “in position” streaks → H sessions; mean/median simple return of
 * overlapping price-only H-session hops. Strategy comparison: mean/median simple return per episode
 * (sale or mark vs cash DCA’d into that position).
 */

function mean(arr) {
  if (!arr?.length) return null;
  const s = arr.reduce((a, b) => a + b, 0);
  return s / arr.length;
}

function median(arr) {
  if (!arr?.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/**
 * Overlapping H-session simple returns: buy at close[i], sell at close[i+H-1].
 * @returns {{ meanPct: number|null, medianPct: number|null, count: number, hUsed: number }}
 */
function overlappingHoldReturns(pxSec, hRaw) {
  const n = pxSec?.length ?? 0;
  if (n < 2) return { meanPct: null, medianPct: null, count: 0, hUsed: 0 };
  let H = Math.max(1, Math.round(Number(hRaw) || 0));
  if (H >= n) H = n - 1;
  if (H < 1) H = 1;

  const rets = [];
  for (let i = 0; i + H - 1 < n; i++) {
    const p0 = pxSec[i].value;
    const p1 = pxSec[i + H - 1].value;
    if (p0 > 0 && Number.isFinite(p0) && Number.isFinite(p1)) {
      rets.push((p1 - p0) / p0);
    }
  }
  const mu = mean(rets);
  const med = median(rets);
  return {
    meanPct: mu != null ? mu * 100 : null,
    medianPct: med != null ? med * 100 : null,
    count: rets.length,
    hUsed: H,
  };
}

/**
 * @param {{ time: number, value: number }[]} pxSec
 * @param {[number, number|null][]} intrinsicPairs — [timeMs, ivPerShare], same length as pxSec
 * @param {{ initialCapital?: number, dcaDailyUsd?: number }} opts
 */
export function runDeviationDcaBacktest(pxSec, intrinsicPairs, opts = {}) {
  const initialCapital = Number(opts.initialCapital);
  const dcaDailyUsd = Number(opts.dcaDailyUsd);
  const initial = Number.isFinite(initialCapital) && initialCapital > 0 ? initialCapital : 10000;
  const dca = Number.isFinite(dcaDailyUsd) && dcaDailyUsd >= 0 ? dcaDailyUsd : 100;

  if (!pxSec?.length || pxSec.length !== intrinsicPairs?.length) {
    return {
      ok: false,
      error: "Need aligned price and intrinsic series.",
      equityCurve: [],
      finalEquity: initial,
      totalReturnPct: 0,
      sellCount: 0,
      initial,
      dcaDaily: dca,
      avgHoldingDaysTrading: null,
      holdingStreakCount: 0,
      benchmarkH: null,
      benchmarkMeanHopReturnPct: null,
      benchmarkMedianHopReturnPct: null,
      benchmarkHopCount: 0,
      strategyMeanEpisodeReturnPct: null,
      strategyMedianEpisodeReturnPct: null,
      strategyEpisodeCount: 0,
    };
  }

  let cash = initial;
  let shares = 0;
  let sellCount = 0;
  const equityCurve = [];
  const holdingStreakLengths = [];
  let runDays = 0;
  /** Cash put into the current position (DCA); reset on full exit. */
  let episodeCost = 0;
  /** Simple return (P&L / cost) for each round-trip or open-position mark. */
  const strategyEpisodeReturns = [];

  for (let i = 0; i < pxSec.length; i++) {
    const price = pxSec[i].value;
    const iv = intrinsicPairs[i]?.[1];
    const tMs = pxSec[i].time * 1000;

    let dev = null;
    if (iv > 0 && Number.isFinite(price) && price > 0) {
      dev = ((price - iv) / iv) * 100;
    }

    if (dev != null && Number.isFinite(dev)) {
      if (dev >= 0) {
        if (shares > 0) {
          const proceeds = shares * price;
          if (episodeCost > 0) {
            strategyEpisodeReturns.push((proceeds - episodeCost) / episodeCost);
          }
          cash += proceeds;
          shares = 0;
          episodeCost = 0;
          sellCount += 1;
        }
      } else {
        const budget = Math.min(dca, cash);
        if (budget > 0) {
          cash -= budget;
          shares += budget / price;
          episodeCost += budget;
        }
      }
    }

    if (shares > 0) {
      runDays += 1;
    } else {
      if (runDays > 0) {
        holdingStreakLengths.push(runDays);
        runDays = 0;
      }
    }

    const eq = cash + shares * (price > 0 ? price : 0);
    equityCurve.push([tMs, eq]);
  }
  if (runDays > 0) holdingStreakLengths.push(runDays);

  const last = pxSec.length - 1;
  const lastPx = pxSec[last]?.value > 0 ? pxSec[last].value : 0;
  const finalEquity = cash + shares * lastPx;
  const totalReturnPct = ((finalEquity - initial) / initial) * 100;

  if (shares > 0 && episodeCost > 0) {
    const markValue = shares * lastPx;
    strategyEpisodeReturns.push((markValue - episodeCost) / episodeCost);
  }

  const strategyMeanEpisodeReturnPct =
    strategyEpisodeReturns.length > 0 ? (mean(strategyEpisodeReturns) ?? 0) * 100 : null;
  const strategyMedianEpisodeReturnPct =
    strategyEpisodeReturns.length > 0 ? (median(strategyEpisodeReturns) ?? 0) * 100 : null;

  const avgHoldingDaysTrading =
    holdingStreakLengths.length > 0
      ? holdingStreakLengths.reduce((a, b) => a + b, 0) / holdingStreakLengths.length
      : null;

  const hop =
    holdingStreakLengths.length > 0 && avgHoldingDaysTrading != null
      ? overlappingHoldReturns(pxSec, avgHoldingDaysTrading)
      : { meanPct: null, medianPct: null, count: 0, hUsed: 0 };

  return {
    ok: true,
    error: null,
    equityCurve,
    finalEquity,
    totalReturnPct,
    sellCount,
    initial,
    dcaDaily: dca,
    avgHoldingDaysTrading,
    holdingStreakCount: holdingStreakLengths.length,
    benchmarkH: hop.hUsed,
    benchmarkMeanHopReturnPct: hop.meanPct,
    benchmarkMedianHopReturnPct: hop.medianPct,
    benchmarkHopCount: hop.count,
    strategyMeanEpisodeReturnPct,
    strategyMedianEpisodeReturnPct,
    strategyEpisodeCount: strategyEpisodeReturns.length,
  };
}
